import React, { useContext, useMemo, useState, useEffect } from 'react'
import { css } from '@emotion/react'
import { rgba } from 'polished'
import Pitch from '../../context/Pitch'
import { colors, fonts, timings, richTextCss } from 'shared-ui'
import ZoneResults from '../test/ZoneResults'
import HealthEffectsMap from '../physicalAssessment/HealthEffectsMap'
import EffectsList from '../physicalAssessment/EffectsList'
import { BodyPartProvider } from '../../context/BodyPart'
import { avgFromZoneResults, rangePercentageFromZoneResults } from '../../utils/analysisCalculations'
import GridModeSwitcher from '../test/GridModeSwitcher'
import { useIntl, FormattedMessage } from 'react-intl'
import AnalysisDate from '../analysis/AnalysisDate'
import PitchEffects from '../physicalAssessment/PitchEffects'
import { SurfaceLayerProvider } from '../../context/SurfaceLayer'
import Modal from '../_layout/Modal'
import dp from 'dompurify'
import IndexGauge from './IndexGauge'
import ToggleableNavLink from '../_control/ToggleableNavLink'
import { setHashParams } from '../../utils/urlHashParams'

const color = colors.main1

export default function IndexResultCard({
    indexData,
    noNavigate = false,
    ...props
}) {
    const { formatMessage } = useIntl()
    const { data: pitch } = useContext(Pitch)

    const [indexModal, setIndexModal] = useState(false)

    // aggregate data
    const [averageMeasurement, rangePercentage] = useMemo(() => {
        return [
            avgFromZoneResults(indexData?.indexZoneResults),
            rangePercentageFromZoneResults(indexData?.indexZoneResults)
        ]
    }, [indexData])

    const hasZoneResults = indexData?.indexZoneResults?.length > 0

    const [showHealthEffects, showPitchEffects] = useMemo(() => {
        const hasHealthEffects = !!indexData?.indexCategory?.hasPhysicalAssessments
        const hasPitchEffects = !!indexData?.indexCategory?.hasPitchAssessments

        if (hasHealthEffects && hasPitchEffects) {
            // if test has configuration for both, show a toggle and only one at a time
            return [hasHealthEffects, false]
        } else {
            // else show whichever has configuration, if any, and no toggle
            return [hasHealthEffects, hasPitchEffects]
        }
    }, [indexData])

    // This index result card assumes an index will have either performance advice or pitch advice but not both.
    const advicesToShow = useMemo(() => {
        const advices = showHealthEffects ?
            indexData?.advicesPerformance
            : showPitchEffects ?
                indexData?.advicesGrounds
                : []
        return advices ?? []
    }, [indexData, showHealthEffects, showPitchEffects])

    const cleanDescription = useMemo(() => {
        return dp.sanitize(indexData?.indexCategory?.description || '')
    }, [indexData])

    useEffect(() => {
        if (indexModal) {
            setHashParams(prev => ({ ...prev, modal: 'indexDescription' }))
        } else {
            setHashParams(prev => {
                const copy = { ...prev }
                delete copy.modal
                return copy
            })
        }
    }, [indexModal])

    if (!indexData) return null

    return (
        <>
            <ToggleableNavLink
                to={(indexData && pitch && !noNavigate) ?
                    `/pitch/${pitch.id}/index/${indexData.id}`
                    : null
                }
                onClick={e => noNavigate && e.preventDefault()}
                css={style.card(color, noNavigate)}
                style={{ textDecoration: 'none' }} /* Necessary for pdf-gen */
                disabled={noNavigate}
                {...props}
            >

                <div
                    onClick={(e) => {
                        e.preventDefault()
                        setIndexModal(true)
                    }}
                    css={style.headerButton}>

                    <IndexGauge
                        size={80}
                        showName={false}
                        indexData={{
                            indexValue: indexData.value,
                            frontendCode: indexData.score?.assessment?.frontendCode,
                            indexType: 'performance',
                        }}
                    />

                    <div css={[style.headerText, css`padding: 1em;`]} >
                        <h4 css={style.title}>
                            {indexData.indexCategory.name}
                        </h4>

                        {(indexData.latestTestTimestamp) &&
                            <h5 css={[style.title, css`opacity: 0.5; margin-left: 0.75rem;`]}>
                                <AnalysisDate
                                    date={indexData.latestTestTimestamp}
                                    timeZone={pitch?.venue?.timeZone}
                                    css={css`font-size: 1em;`}
                                />
                            </h5>
                        }
                    </div>
                </div>

                <hr css={style.hr} />

                {/* Main content */}
                <BodyPartProvider><SurfaceLayerProvider>
                    <div css={style.mainContent}>


                        {(showHealthEffects || showPitchEffects) &&
                            <div>
                                <h3 css={style.effectHeader}>
                                    {formatMessage({ id: 'athlete' })}
                                </h3>
                                {showHealthEffects &&
                                    <div css={style.effectsMap}>
                                        <HealthEffectsMap tests={[indexData]} />
                                    </div>
                                }
                                {showPitchEffects &&
                                    <div style={{ alignSelf: 'stretch', fontSize: '0.85em' }}>
                                        <PitchEffects tests={[indexData]} />
                                    </div>
                                }
                            </div>
                        }

                        {(showHealthEffects || showPitchEffects) &&
                            <div css={css` min-width: 23em; `}>
                                {showHealthEffects && <>
                                    <h3 css={style.effectsHeader2}>
                                        <FormattedMessage id='performanceAndWelfareInfluencers' />
                                    </h3>
                                    <EffectsList
                                        tests={[indexData]}
                                        type='player'
                                    />
                                </>}
                                {showPitchEffects && <>
                                    <h3 css={style.effectsHeader2}>
                                        <FormattedMessage id='surfaceInfluencers' />
                                    </h3>
                                    <EffectsList
                                        tests={[indexData]}
                                        type='pitch'
                                    />
                                </>}
                            </div>
                        }

                        {hasZoneResults &&
                            <div css={style.zoneResults}>
                                <h3 css={style.effectsHeader2}>
                                    {formatMessage({ id: 'zones' })}
                                </h3>
                                <ZoneResults
                                    gridId={indexData?.id}
                                    backgroundType={pitch?.pitchBackground || pitch?.club?.sport}
                                    css={style.field}
                                    zoneResults={indexData?.indexZoneResults}
                                    isPercentageTemplate={indexData?.indexCategory?.pitchTemplate?.templateType === 'percentage'}
                                    decimals={1}
                                    getZoneBackendColor={(zone) => (zone.rating?.assessment?.frontendCode)}
                                />

                                <div css={style.aggregates}>
                                    <GridModeSwitcher />
                                    {rangePercentage !== undefined &&
                                        <div css={css`font-size: 0.9375em; text-transform: capitalize;`}>
                                            <FormattedMessage id='dataRange' />
                                            {': ' + rangePercentage}
                                        </div>
                                    }
                                    {averageMeasurement !== undefined &&
                                        <div css={css`font-size: 0.9375em;`}>
                                            <FormattedMessage id='averageValue' />
                                            {': ' + averageMeasurement}
                                        </div>
                                    }
                                </div>
                            </div>
                        }


                        <div css={css`margin-bottom: 1em; min-width: 22em; width: 100%;`}>
                            {!!(advicesToShow.length) && <>
                                <h3 css={style.effectsHeader2}>
                                    <FormattedMessage id='advice' />
                                </h3>
                                {advicesToShow.map(({ id, instruction }) => {
                                    const cleanInstruction = dp.sanitize(instruction)
                                    return <div key={id} dangerouslySetInnerHTML={{ __html: cleanInstruction }} />
                                })}
                            </>}
                        </div>
                    </div>

                </SurfaceLayerProvider></BodyPartProvider>
            </ToggleableNavLink>

            {indexModal &&
                <Modal onClickClose={() => setIndexModal(false)} >
                    <div css={css`display:flex; align-items: center; gap: 0.75rem; margin-bottom: 1em;`}>
                        <div css={style.headerText} >
                            <h4 css={style.title}>
                                {indexData.localisedName}
                            </h4>
                        </div>
                    </div>

                    <div
                        css={css`${richTextCss}`}
                        dangerouslySetInnerHTML={{ __html: cleanDescription }}
                    />
                </Modal>
            }
        </>
    )
}

const style = {
    card: (color, noNavigate) => css`
        cursor: ${noNavigate ? 'unset' : ''};
        text-decoration: none;
        display: block;
        border-radius: 0.3125em;
        overflow: hidden;
        padding: 1.25em;
        color: ${colors.white};
        position: relative;
        z-index: 1;
        transition: all ${timings.responsive} ease;
        box-shadow: 0 0.25em 0.75em 0 rgba(0,0,0,0.3);
        &::before {
            content: '';
            position: absolute;
            display: block;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            background-color: ${colors.stuff};
            z-index: -2;
            transition: all ${timings.responsive} ease;
            opacity: 0.66;
        }
        &::after {
            content: '';
            position: absolute;
            display: block;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            background-image: linear-gradient(170deg, rgba(255,255,255,0) 50%, ${color});
            z-index: -1;
            transition: all ${timings.responsive} ease;
            opacity: 0.15;
        }
        ${!noNavigate ?
            `
            &:hover {
                transition: all ${timings.snappy} ease;
                box-shadow: 0 0.75em 1.5em 0 ${rgba(colors.black, 0.5)};
                &::before {
                    transition: all ${timings.snappy} ease;
                    opacity: 1;
                    background-color: ${colors.solid};
                }
                &::after {
                    transition: all ${timings.snappy} ease;
                    opacity: 0.25;
                }
            }
            ` : ''
        }
        
    `,
    titleHeader: css`
        display:flex; 
        align-items: center; 
        gap: 0.75rem;
        border-radius: 100px;

        &:hover {
            background: rgba(0,0,0,0.15);
            cursor: pointer;
        }

    `,
    title: css`
        display: inline-block;
        font-size: 1.25em;
        line-height: 1.2;
        font-family: ${fonts.special};
        font-weight: 700;
        text-transform: uppercase;
    `,
    field: css`
        overflow: hidden;
        background-color: ${rgba(colors.light, 0.1)};
        border-radius: 0.3125em;
        padding: 0.5em;
    `,
    aggregates: css`
        display: flex;
        justify-content: space-between;
        margin-top: 0.2em;
        align-items: center;
        flex-direction: row-reverse;
    `,
    headerText: css`
        display: flex;
        flex-grow: 1; 
        flex-shrink: 1; 
        align-items: center;
        justify-content: space-between;
    `,
    mainContent: css`
        padding: 1em;
        display: flex;
        gap: 2em;

        @media screen and (max-width: 1800px) {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 2em;
        }

        @media screen and (max-width: 800px) {
            display: flex; 
            flex-direction: column;
            align-items: start;
            
            > * {
                min-width: 320px;
                max-width: 600px;
                margin-bottom: 1em;
            }
        }
    `,
    headerButton: css`
        display: flex;
        align-items: center;
        border-radius: 5px;
        padding: 0.2em 1em;
        background: rgba(56,66,74, 0.8);

        &:hover {
            background: rgba(0, 0, 0, 0.1);
        }
    `,
    hr: css`
        width: 100%;
        opacity: 0.1;
        color: ${colors.light};    
    `,
    effectHeader: css`
        font-size: 1.75em; 
        font-weight: 200; 
        line-height: 1;
        margin-bottom: 0.875em; 
        font-family: ${fonts.special}; color: ${colors.subtle};
        place-self: start;
    `,
    effectsMap: css`
        width: 23em;
        font-size: 0.85em;

        @media screen and (max-width: 600px) {
            width: min(100%, 300px);
        }
    `,
    zoneResults: css`
        min-width: 25em; 
        max-width: 25em;

        @media screen and (max-width: 800px) {
            width: min(100%, 300px);
        }
    `,
    effectsHeader2: css`
        font-size: 1.75em; 
        font-weight: 200; 
        line-height: 1; 
        margin-bottom: 0.875em; 
        font-family: ${fonts.special}; 
        color: ${colors.subtle};
    `
}